import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { writeFetch, getSession } from '../../../api';

const {
  FiShield, FiPlus, FiEdit2, FiTrash2, FiCheck, FiX,
  FiRefreshCw, FiToggleLeft, FiToggleRight, FiStar,
  FiAward, FiZap, FiLock, FiTruck, FiHeart, FiEye,
  FiAlertTriangle, FiTag,
} = FiIcons;

const TABLE = 'merchant_trust_badges_1776540011';

// Icon picker options mapped to react-icons
const ICON_OPTIONS = [
  { key: 'FiShield',        icon: FiShield,        label: 'Shield'        },
  { key: 'FiAward',         icon: FiAward,         label: 'Award'         },
  { key: 'FiStar',          icon: FiStar,          label: 'Star'          },
  { key: 'FiCheck',         icon: FiCheck,         label: 'Check'         },
  { key: 'FiZap',           icon: FiZap,           label: 'Lightning'     },
  { key: 'FiLock',          icon: FiLock,          label: 'Lock'          },
  { key: 'FiTruck',         icon: FiTruck,         label: 'Truck'         },
  { key: 'FiHeart',         icon: FiHeart,         label: 'Heart'         },
  { key: 'FiEye',           icon: FiEye,           label: 'Eye'           },
  { key: 'FiAlertTriangle', icon: FiAlertTriangle, label: 'Alert'         },
  { key: 'FiTag',           icon: FiTag,           label: 'Tag'           },
  { key: 'FiRefreshCw',     icon: FiRefreshCw,     label: 'Returns'       },
];

const COLOR_OPTIONS = [
  { value: '#D4AF37', label: 'Gold'        },
  { value: '#1a3a5c', label: 'Navy'        },
  { value: '#16a34a', label: 'Green'       },
  { value: '#2563eb', label: 'Blue'        },
  { value: '#7c3aed', label: 'Purple'      },
  { value: '#dc2626', label: 'Red'         },
  { value: '#ea580c', label: 'Orange'      },
  { value: '#0891b2', label: 'Teal'        },
  { value: '#64748b', label: 'Slate'       },
];

const EMPTY_FORM = {
  label: '',
  description: '',
  icon: 'FiShield',
  color: '#D4AF37',
  is_active: 1,
  sort_order: 0,
};

// ── Resolve icon component from string key ─────────────────────────────────
const resolveIcon = (key) =>
  ICON_OPTIONS.find(o => o.key === key)?.icon || FiShield;

// ── Badge Preview Card ─────────────────────────────────────────────────────
const BadgePreview = ({ badge }) => {
  const Icon = resolveIcon(badge.icon);
  return (
    <div className="flex flex-col items-center gap-1.5 px-3 py-2 bg-brand-light rounded-2xl border border-brand-dark min-w-[80px]">
      <div className="w-8 h-8 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: `${badge.color}20` }}>
        <SafeIcon icon={Icon} className="w-4 h-4" style={{ color: badge.color }} />
      </div>
      <span className="text-[9px] font-black uppercase tracking-widest text-text-muted text-center leading-tight">
        {badge.label || 'Badge'}
      </span>
    </div>
  );
};

// ── Badge Form Modal ───────────────────────────────────────────────────────
const BadgeFormModal = ({ badge, onClose, onSave }) => {
  const [form, setForm] = useState(badge || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.label.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
    onClose();
  };

  const PreviewIcon = resolveIcon(form.icon);

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div className="p-6 border-b border-brand-light flex justify-between items-center bg-brand-light/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent-navy rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiShield} className="text-accent-gold w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-serif text-accent-navy">
                {badge ? 'Edit Badge' : 'New Trust Badge'}
              </h3>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
                {badge ? 'Update badge details' : 'Create a new trust badge'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Live Preview */}
          <div className="flex items-center gap-4 p-4 bg-brand-light rounded-2xl border border-brand-dark">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: `${form.color}20` }}>
              <SafeIcon icon={PreviewIcon} className="w-6 h-6" style={{ color: form.color }} />
            </div>
            <div>
              <p className="text-sm font-bold text-accent-navy">{form.label || 'Badge Label'}</p>
              <p className="text-[10px] text-text-muted font-light">{form.description || 'Badge description'}</p>
            </div>
          </div>

          {/* Label */}
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Badge Label *</label>
            <input value={form.label} onChange={e => set('label', e.target.value)}
              placeholder="e.g. Secure Payment"
              className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm" />
          </div>

          {/* Description */}
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Description</label>
            <input value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="e.g. All payments are SSL encrypted"
              className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm" />
          </div>

          {/* Icon Picker */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Icon</label>
            <div className="grid grid-cols-6 gap-2">
              {ICON_OPTIONS.map(opt => (
                <button key={opt.key} onClick={() => set('icon', opt.key)}
                  title={opt.label}
                  className={`p-2.5 rounded-xl flex flex-col items-center gap-1 border-2 transition-all ${
                    form.icon === opt.key
                      ? 'border-accent-gold bg-accent-gold/10'
                      : 'border-brand-dark hover:border-accent-gold/50'
                  }`}>
                  <SafeIcon icon={opt.icon} className="w-4 h-4 text-accent-navy" />
                  <span className="text-[7px] font-black uppercase text-text-muted">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Color Picker */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Color</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map(c => (
                <button key={c.value} onClick={() => set('color', c.value)}
                  title={c.label}
                  className={`w-9 h-9 rounded-xl border-2 transition-all ${
                    form.color === c.value ? 'border-accent-navy scale-110' : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: c.value }}>
                  {form.color === c.value && (
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-white mx-auto" />
                  )}
                </button>
              ))}
              {/* Custom hex */}
              <div className="flex items-center gap-2">
                <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
                  className="w-9 h-9 rounded-xl cursor-pointer border-2 border-brand-dark" />
                <span className="text-[10px] font-mono text-text-muted">{form.color}</span>
              </div>
            </div>
          </div>

          {/* Sort Order */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Sort Order</label>
              <input type="number" min="0" value={form.sort_order}
                onChange={e => set('sort_order', parseInt(e.target.value) || 0)}
                className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Status</label>
              <button onClick={() => set('is_active', form.is_active ? 0 : 1)}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 transition-all ${
                  form.is_active
                    ? 'bg-green-50 border-green-300 text-green-700'
                    : 'bg-red-50 border-red-200 text-red-600'
                }`}>
                <SafeIcon icon={form.is_active ? FiToggleRight : FiToggleLeft} className="w-4 h-4" />
                {form.is_active ? 'Active' : 'Inactive'}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-3 border-2 border-brand-dark rounded-xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-navy transition-all">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !form.label.trim()}
            className="flex-1 py-3 bg-accent-gold text-accent-navy rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50">
            {saving
              ? <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
              : <><SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /> {badge ? 'Save Changes' : 'Create Badge'}</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ── Main TrustBadgesTab ────────────────────────────────────────────────────
const TrustBadgesTab = () => {
  const user = getSession();
  const [badges, setBadges]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editingBadge, setEditing]  = useState(null); // null=closed, false=new, obj=edit
  const [deletingId, setDeletingId] = useState(null);

  const fetchBadges = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res  = await writeFetch(`read/${TABLE}?owner_id=${user.id}&limit=100&sort=sort_order&order=asc`);
      const data = await res.json();
      if (data.status === 'success') setBadges(data.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { fetchBadges(); }, [fetchBadges]);

  const handleSave = async (form) => {
    const now = new Date().toISOString();
    if (editingBadge && editingBadge.id) {
      // Update
      await writeFetch(`update/${TABLE}/${editingBadge.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...form, updated_at: now }),
      });
    } else {
      // Create
      await writeFetch(`create/${TABLE}`, {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          owner_id:   user.id,
          created_at: now,
          updated_at: now,
        }),
      });
    }
    await fetchBadges();
  };

  const handleToggle = async (badge) => {
    await writeFetch(`update/${TABLE}/${badge.id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: badge.is_active ? 0 : 1, updated_at: new Date().toISOString() }),
    });
    setBadges(prev => prev.map(b => b.id === badge.id ? { ...b, is_active: badge.is_active ? 0 : 1 } : b));
  };

  const handleDelete = async (badge) => {
    if (!window.confirm(`Delete "${badge.label}"?`)) return;
    setDeletingId(badge.id);
    await writeFetch(`delete/${TABLE}/${badge.id}`, { method: 'DELETE' });
    setBadges(prev => prev.filter(b => b.id !== badge.id));
    setDeletingId(null);
  };

  const activeBadges   = badges.filter(b => b.is_active);
  const inactiveBadges = badges.filter(b => !b.is_active);

  return (
    <div className="space-y-6">
      {/* Stats + Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-4">
          <div className="bg-white p-5 rounded-[24px] border border-brand-dark shadow-sm min-w-[120px]">
            <p className="text-2xl font-serif text-accent-navy">{badges.length}</p>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-1">Total Badges</p>
          </div>
          <div className="bg-white p-5 rounded-[24px] border border-brand-dark shadow-sm min-w-[120px]">
            <p className="text-2xl font-serif text-green-600">{activeBadges.length}</p>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-1">Active</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={fetchBadges}
            className="p-3 bg-white border-2 border-brand-dark rounded-2xl hover:border-accent-gold text-text-muted hover:text-accent-navy transition-all">
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
          </button>
          <button onClick={() => setEditing(false)}
            className="flex items-center gap-2 px-6 py-3 bg-accent-gold text-accent-navy rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all shadow-lg">
            <SafeIcon icon={FiPlus} className="w-4 h-4" /> New Badge
          </button>
        </div>
      </div>

      {/* Marketplace Preview */}
      {activeBadges.length > 0 && (
        <div className="bg-white p-6 rounded-[32px] border border-brand-dark shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-4">
            Preview — How badges appear on product cards
          </p>
          <div className="flex flex-wrap gap-3">
            {activeBadges.slice(0, 5).map(b => <BadgePreview key={b.id} badge={b} />)}
            {activeBadges.length > 5 && (
              <div className="flex flex-col items-center justify-center px-3 py-2 bg-brand-light rounded-2xl border border-brand-dark min-w-[80px]">
                <span className="text-sm font-serif text-accent-navy">+{activeBadges.length - 5}</span>
                <span className="text-[9px] font-black uppercase tracking-widest text-text-muted">more</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Badge List */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-[28px] border border-brand-dark p-5 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-brand-light rounded-2xl" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-brand-light rounded-full w-3/4" />
                  <div className="h-3 bg-brand-light rounded-full w-1/2" />
                </div>
              </div>
              <div className="h-8 bg-brand-light rounded-xl" />
            </div>
          ))}
        </div>
      ) : badges.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[40px] border-2 border-dashed border-brand-dark gap-4">
          <div className="w-16 h-16 bg-brand-light rounded-3xl flex items-center justify-center">
            <SafeIcon icon={FiShield} className="w-8 h-8 text-text-muted" />
          </div>
          <div className="text-center">
            <p className="text-xl font-serif text-accent-navy mb-1">No Trust Badges Yet</p>
            <p className="text-sm text-text-muted font-light">Create badges like "Secure Payment" or "Easy Returns" to build buyer confidence.</p>
          </div>
          <button onClick={() => setEditing(false)}
            className="px-6 py-3 bg-accent-gold text-accent-navy rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all">
            Create First Badge
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active Badges */}
          {activeBadges.length > 0 && (
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Active Badges ({activeBadges.length})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeBadges.map(badge => (
                  <BadgeCard key={badge.id} badge={badge}
                    onEdit={() => setEditing(badge)}
                    onToggle={() => handleToggle(badge)}
                    onDelete={() => handleDelete(badge)}
                    isDeleting={deletingId === badge.id} />
                ))}
              </div>
            </div>
          )}

          {/* Inactive Badges */}
          {inactiveBadges.length > 0 && (
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-400 inline-block" /> Inactive Badges ({inactiveBadges.length})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">
                {inactiveBadges.map(badge => (
                  <BadgeCard key={badge.id} badge={badge}
                    onEdit={() => setEditing(badge)}
                    onToggle={() => handleToggle(badge)}
                    onDelete={() => handleDelete(badge)}
                    isDeleting={deletingId === badge.id} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {editingBadge !== null && (
          <BadgeFormModal
            badge={editingBadge || null}
            onClose={() => setEditing(null)}
            onSave={handleSave} />
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Badge Card ─────────────────────────────────────────────────────────────
const BadgeCard = ({ badge, onEdit, onToggle, onDelete, isDeleting }) => {
  const Icon = resolveIcon(badge.icon);
  return (
    <motion.div layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-[28px] border border-brand-dark shadow-sm overflow-hidden group hover:border-accent-gold transition-all">
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${badge.color}20` }}>
            <SafeIcon icon={Icon} className="w-6 h-6" style={{ color: badge.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-accent-navy text-sm truncate">{badge.label}</h4>
            <p className="text-[10px] text-text-muted font-light mt-0.5 line-clamp-2">{badge.description || '—'}</p>
          </div>
          <span className={`flex-shrink-0 text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full ${
            badge.is_active
              ? 'bg-green-50 text-green-600 border border-green-200'
              : 'bg-red-50 text-red-500 border border-red-200'
          }`}>
            {badge.is_active ? 'Active' : 'Off'}
          </span>
        </div>

        {/* Meta */}
        <div className="flex items-center gap-3 mb-4 text-[10px] text-text-muted">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: badge.color }} />
            <span className="font-mono">{badge.color}</span>
          </div>
          <span>·</span>
          <span>Order: {badge.sort_order}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button onClick={onEdit}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-brand-light rounded-xl text-[10px] font-black uppercase tracking-widest text-accent-navy hover:bg-accent-navy hover:text-white transition-all">
            <SafeIcon icon={FiEdit2} className="w-3 h-3" /> Edit
          </button>
          <button onClick={onToggle}
            className={`p-2 rounded-xl transition-all border ${
              badge.is_active
                ? 'bg-green-50 border-green-200 text-green-600 hover:bg-red-50 hover:border-red-200 hover:text-red-500'
                : 'bg-red-50 border-red-200 text-red-500 hover:bg-green-50 hover:border-green-200 hover:text-green-600'
            }`}
            title={badge.is_active ? 'Deactivate' : 'Activate'}>
            <SafeIcon icon={badge.is_active ? FiToggleRight : FiToggleLeft} className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} disabled={isDeleting}
            className="p-2 bg-brand-light rounded-xl text-text-muted hover:bg-red-50 hover:text-red-500 transition-all border border-brand-dark hover:border-red-200 disabled:opacity-50">
            {isDeleting
              ? <div className="w-3.5 h-3.5 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
              : <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default TrustBadgesTab;